import * as React from 'react';

import styled, { css } from 'styled-components';

import { Option } from '@webapp/types/common/option';

import InputField from '@components/form/input';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';

import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';

export interface SearchbarProps<T, U = string> {
  id?: string;
  className?: string;
  wrapperClassName?: string;
  label?: React.ReactNode;
  value: React.ReactNode;
  results: T[];
  fullList?: T[];
  filterList?: (t: T) => boolean;
  decoration?: any;
  locked?: boolean;
  error?: string;
  customBorderRadius?: string;
  noShadow?: boolean;
  parentRelative?: boolean;
  hasTruncate?: boolean;
  hasClose?: boolean;
  isNameOnlyInput?: boolean;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  search: (value: string) => void;
  resetSearch?: () => void;
  onChange: (value: string) => void;
  onConfirmName?: () => void;
  onSelection: (value: T) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onResultsChange?: (results: T[]) => void;
  valueMapper?: (value: T) => string;
  resultMapper?: (value: T) => string;
  keyMapper?: (value: T) => string;
  decorationMapper?: (value: T) => React.ReactElement | null;
  rightDecorationMapper?: (value: T) => React.ReactElement | null;
  helpTextMapper?: (value: T) => string;
  queryMapper?: (value: U) => string;
  leftDecoration?: any;
  headerIcon?: boolean;
  dropdownListPadding?: string;
  type?: string;
  currentValues?: any[];
}

export const SearchbarBuilder = <T, U = string>(Input?: any) => {
  const DropdownCtrl = GenericDropdownBuilder<T>();
  Input = Input ? Input : InputField;

  function Searchbar(props: SearchbarProps<T, U>) {
    const [timer, setTimer] = React.useState<number | null>(null);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [arrow, setArrow] = React.useState(false);
    const { results, onResultsChange } = props;

    React.useEffect(() => {
      if (onResultsChange) {
        onResultsChange(results);
      }
    }, [results, onResultsChange]);

    const handleChange = (
      value: string,
      ev?: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (!props.search) {
        return;
      }

      props.onChange(value);

      let query: string = '';

      if (typeof value === 'string') {
        query = value;
      } else {
        if (props.queryMapper) {
          query = props.queryMapper(value);
        }
      }

      if (query.length < 2) {
        return;
      }

      if (ev) {
        ev.persist();
      }

      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }

      setTimer(window.setTimeout(() => props.search(query), 500));
    };

    const handleClick = (value: T) => {
      if (props.onSelection) {
        props.onSelection(value);
        if (props?.resetSearch) {
          props.resetSearch();
        }
      }
    };

    const getLabel = (value: T): string => {
      if (props.resultMapper) {
        return props.resultMapper(value);
      }
      if (props.valueMapper) {
        return props.valueMapper(value);
      }

      return value as unknown as string;
    };

    const getResults = (): Option<T>[] => {
      let resultsList: Option<T>[] = [];
      if (props.results && props.results.length) {
        resultsList = resultsList.concat(
          props.results
            .filter((r) => !resultsList.find((t) => t.value === r))
            // .filter((t) => (props.filterList ? props.filterList(t) : true))
            .map((r) => ({
              label: getLabel(r),
              value: r,
              decoration: getDecoration(r),
              rightDecoration: getRightDecoration(r),
              helpText: getHelpText(r),
            }))
        );
      }
      return resultsList;
    };

    const getDecoration = (value: T): React.ReactElement | undefined => {
      if (props.decorationMapper) {
        return props.decorationMapper(value) || undefined;
      }

      return undefined;
    };
    const getRightDecoration = (value: T): React.ReactElement | undefined => {
      if (props.rightDecorationMapper) {
        return props.rightDecorationMapper(value) || undefined;
      }

      return undefined;
    };

    const getHelpText = (value: T): string | undefined => {
      if (props.helpTextMapper) {
        return props.helpTextMapper(value) || '';
      }

      return '';
    };

    const calculatedResults = React.useMemo(getResults, [
      props.fullList,
      props.results,
      props.value,
    ]);

    const checkAlreadyExist = (calculatedResults) => {
      return calculatedResults?.map((calculatedResult) => {
        if (!props.currentValues) {
          return calculatedResult;
        }
        if (
          props?.currentValues?.some((curValue) => {
            switch (props.type) {
              case 'songwriter':
                return curValue?.fullName === calculatedResult?.value?.name;
              case 'owner':
                return curValue?.fullName === calculatedResult?.value?.name;
              case 'collaborator':
                return curValue?.name === calculatedResult?.value?.name;
              case 'credits':
                return curValue?.name === calculatedResult?.value?.name;
              default:
                return curValue?.email === calculatedResult?.value?.email;
            }
          })
        ) {
          return {
            ...calculatedResult,
            value: { ...calculatedResult.value, alreadyExists: true },
          };
        }
        return {
          ...calculatedResult,
          value: { ...calculatedResult.value, alreadyExists: false },
        };
      });
    };

    return (
      <div
        className={props.wrapperClassName}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      >
        <DropdownCtrl
          options={checkAlreadyExist(calculatedResults)}
          onChange={(value: T) => handleClick(value)}
          forceOpen={!!calculatedResults.length && hasFocus}
          label={props.value}
          type={props.type}
          isNameOnlyInput={props.isNameOnlyInput}
          onOpen={() => setArrow(true)}
          onClose={() => setArrow(false)}
          className={props.className}
          keyMapper={props.keyMapper}
          valueMapper={props.valueMapper}
          parentRelative={props.parentRelative}
          customBorderRadius={props.customBorderRadius}
          customListPadding={props.dropdownListPadding}
          onConfirmName={props.onConfirmName}
          header={
            <HeaderWrapper>
              <Input
                id={props.id}
                className={`input ${
                  props.customBorderRadius ? 'custom-radius' : ''
                }`}
                name="searchbar"
                autoComplete="off"
                value={props.value}
                label={props.label}
                locked={props.locked}
                error={props.error}
                onChange={(
                  value: string,
                  ev: React.ChangeEvent<HTMLInputElement>
                ) => handleChange(value, ev)}
                rightDecoration={props.decoration}
                hasClose={props.hasClose}
                onClose={props.onClose}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                noShadow={props.noShadow}
                hasTruncate={props.hasTruncate}
                propagateClick={!!props.results?.length}
                leftDecoration={props.leftDecoration}
              />
              {props.headerIcon ? (
                <CircleArrowIcon
                  className="dropdown-icon"
                  size="24px"
                  arrowUp={arrow}
                />
              ) : null}
            </HeaderWrapper>
          }
        />
      </div>
    );
  }

  return styled((props: SearchbarProps<T, U>) => <Searchbar {...props} />)`
    ${(props) => (props.parentRelative ? null : 'position: relative;')}
    ${(props) =>
      props.customBorderRadius
        ? css`
            .input {
              &.custom-radius {
                border-radius: ${props.customBorderRadius};
              }
            }
          `
        : null}
  `;
};
const HeaderWrapper = styled.div`
  position: relative;
  .dropdown-icon {
    position: absolute;
    right: 10px;
    top: 30%;
  }
`;
const BasicSearchBar = SearchbarBuilder<string>();

export default BasicSearchBar;
